import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowsPointingOutIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../common/LoadingSpinner";
import {
  cancelExpansion,
  confirmExpansion,
  expansionErrorText,
  getExpansionStatus,
  prepareExpansion,
  verifyExpansionDesign,
  type ExpansionProposal,
  type ExpansionRequest,
  type ExpansionStatus,
  type ExpansionWave,
} from "../../services/eibExpansion.service";

/**
 * Event in a Box — "Expand invitation radius" (design v4, migrations 2026-10-13-460..464).
 *
 * Widening invites ONLY people who have not been invited to this event yet (checked by person and by
 * email address), plus whichever follow-ups are still ahead. Everything is decided on the server; this
 * panel explains the numbers and carries the operator's choices.
 */

const LIVE = new Set(["building", "previewing", "ready", "confirmed"]);

const EXCLUSION_LABELS: Record<string, string> = {
  registered: "Already registered for this event",
  already_mailed: "Already sent this event's invitation",
  in_unsent_wave:
    "In an earlier wave that has not gone out yet (left out so nobody gets two invitations)",
  address_already_used:
    "Their email address already received this event's invitation",
  address_in_unsent_wave:
    "Their email address belongs to someone in a wave that has not gone out yet",
  merged_identity: "Duplicate record of someone already invited",
  reserved_never_mailed_excluded:
    "In an earlier wave but never reached (not included — see the option below)",
  inside_radius_left_out: "Inside the area you chose to leave out",
};

function mi(n: number | null | undefined): string {
  if (n === null || n === undefined) {
    return "—";
  }
  return `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })} mi`;
}

function fmtDate(iso: string | null | undefined, tz?: string | null): string {
  if (!iso) {
    return "not scheduled";
  }
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  try {
    return new Date(iso).toLocaleString(
      undefined,
      tz ? { ...opts, timeZone: tz } : opts,
    );
  } catch {
    return new Date(iso).toLocaleString(undefined, opts);
  }
}

function waveLine(w: ExpansionWave): string {
  if (w.qualifies && w.rule_radius_mi !== null) {
    return `Wave ${w.wave_number}: ${mi(w.rule_radius_mi)} (audience rule)`;
  }
  const m = w.measured;
  if (!m || m.max_mi === null) {
    return `Wave ${w.wave_number}: custom list (members have no map location to measure)`;
  }
  let s = `Wave ${w.wave_number}: custom list, members out to ${mi(m.robust_mi)} (measured`;
  if (m.p95_mi !== null && !m.outlier) {
    s += `; 95% within ${mi(m.p95_mi)}`;
  }
  s += ")";
  if (m.outlier) {
    s += ` · farthest member ${mi(m.max_mi)}, likely an address error`;
  }
  return s;
}

// -----------------------------------------------------------------------------------------------
// Coverage summary + entry button
// -----------------------------------------------------------------------------------------------
function CoverageSummary({ status }: { status: ExpansionStatus }) {
  const lines = status.waves.map(waveLine);
  status.expansions.forEach((x) =>
    lines.push(
      `Radius expansion ${x.expansion_number ?? ""}: ${mi(x.new_radius_mi)} (audience rule)`,
    ),
  );
  return (
    <div>
      <p
        className="text-base font-semibold text-gray-900"
        data-testid="eib-expand-headline"
      >
        {status.headline.headline_mi > 0
          ? `Already invited out to ${status.headline.headline_mi} mi`
          : "No invitation radius yet"}
      </p>
      {lines.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}
      {status.waves.some((w) => !w.qualifies) && (
        <p
          className="mt-1 text-xs text-gray-500"
          title="This wave was built by hand, so this is how far its members actually are, not a rule."
        >
          “Measured” means the wave was built by hand, so this is how far its
          members actually are, not a rule.
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------------------------
// Choose radius (prepare)
// -----------------------------------------------------------------------------------------------
function ExpandForm({
  eventId,
  status,
  onDone,
  onCancel,
}: {
  eventId: string;
  status: ExpansionStatus;
  onDone: () => void;
  onCancel: () => void;
}) {
  const source = status.source.source_radius_mi ?? 0;
  const ruleCovered = status.rule_covered_mi ?? source;
  const headline = status.headline.headline_mi;
  const [radius, setRadius] = useState<string>(
    String(Math.max(Math.ceil(headline) + 5, Math.ceil(source) + 5)),
  );
  const [includeInside, setIncludeInside] = useState(true);
  const [neverMailed, setNeverMailed] = useState(false); // owner decision D11: OFF by default
  const [key] = useState(() => crypto.randomUUID());
  const r = Number(radius);
  const defaultInside = ruleCovered < r ? ruleCovered : source;
  const [insideText, setInsideText] = useState<string>("");
  const inside = includeInside
    ? null
    : insideText === ""
      ? defaultInside
      : Number(insideText);
  const radiusError =
    !Number.isFinite(r) || r <= source
      ? `Choose a radius wider than the original audience rule (${mi(source)}).`
      : r > 500
        ? "The radius can be at most 500 miles."
        : null;
  const insideError =
    inside !== null &&
    (!Number.isFinite(inside) || inside < source || inside >= r)
      ? `The area to leave out must be between ${mi(source)} and the new radius.`
      : null;
  const prep = useMutation({
    mutationFn: () =>
      prepareExpansion({
        eventId,
        idempotencyKey: key,
        newRadius: r,
        insideRadius: inside,
        includeNeverMailed: neverMailed,
      }),
    onSuccess: onDone,
  });
  return (
    <div
      className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 space-y-4"
      data-testid="eib-expand-form"
    >
      <div>
        <label
          className="block text-sm font-medium text-gray-800"
          htmlFor="eib-expand-radius"
        >
          New radius (miles)
        </label>
        <input
          id="eib-expand-radius"
          type="number"
          min={source + 0.1}
          max={500}
          step={1}
          value={radius}
          onChange={(e) => setRadius(e.target.value)}
          className="mt-1 w-32 rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          Same audience rule as the original invitation ({mi(source)} around the
          same centre), just wider.
        </p>
        {radiusError && (
          <p className="mt-1 text-xs text-red-700">{radiusError}</p>
        )}
        {!radiusError && r <= headline && (
          <p className="mt-1 flex items-start gap-1 text-xs text-amber-800">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
            This is not wider than the area already covered ({headline} mi). It
            will mostly find people who joined or became eligible since. You can
            still go ahead; the preview asks you to confirm.
          </p>
        )}
      </div>

      <div>
        <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={includeInside}
            onChange={(e) => setIncludeInside(e.target.checked)}
          />
          <span>
            Also invite people inside the area already covered who were not
            invited then
            <span className="block text-xs text-gray-500">
              Mostly members who joined, or became eligible, since the first
              invitation.
            </span>
          </span>
        </label>
        {!includeInside && (
          <div className="ml-6 mt-2">
            <label
              className="text-xs text-gray-700"
              htmlFor="eib-expand-inside"
            >
              Off: leaves out people within
            </label>{" "}
            <input
              id="eib-expand-inside"
              type="number"
              step={0.1}
              value={insideText === "" ? String(defaultInside) : insideText}
              onChange={(e) => setInsideText(e.target.value)}
              className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />{" "}
            <span className="text-xs text-gray-700">mi</span>
            {insideError && (
              <p className="mt-1 text-xs text-red-700">{insideError}</p>
            )}
          </div>
        )}
      </div>

      <div>
        <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={neverMailed}
            onChange={(e) => setNeverMailed(e.target.checked)}
            data-testid="eib-expand-never-mailed"
          />
          <span>
            Include earlier waves’ members who were never mailed
            <span className="block text-xs text-gray-500">
              Earlier invitations could not reach these people (no email,
              suppressed, invalid address, unsubscribed from the group). Turning
              this on invites them now; most will be filtered again at send.
              Members of a wave that has not gone out yet are never included
              either way. The preview shows how many.
            </span>
          </span>
        </label>
      </div>

      {prep.error && (
        <p className="text-sm text-red-700">{expansionErrorText(prep.error)}</p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!!radiusError || !!insideError || prep.isPending}
          onClick={() => prep.mutate()}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {prep.isPending ? "Starting…" : "Build preview"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm"
        >
          Close
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Nothing is reserved or sent until you confirm the preview.
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------------------------
// Preview + confirm
// -----------------------------------------------------------------------------------------------
function PreviewTable({
  p,
  req,
}: {
  p: ExpansionProposal;
  req: ExpansionRequest;
}) {
  const excludedRows = Object.entries(p.excluded ?? {}).filter(
    ([, n]) => n > 0,
  );
  const followups = p.offsets ?? [];
  const sendWhy =
    p.send.branch === "0800"
      ? "08:00 event time"
      : p.send.branch === "next_hour"
        ? "the next hour, because the event is today or tomorrow"
        : "08:00, because the next hour falls in quiet hours (9 PM–8 AM)";
  return (
    <div className="space-y-3 text-sm" data-testid="eib-expand-preview">
      <p className="text-xs text-gray-500">
        All counts are before send-time checks (suppression, invalid addresses).
      </p>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          <tr>
            <td className="py-1 text-gray-700">
              In the {mi(req.new_radius_mi)} area now (same audience rule)
            </td>
            <td className="py-1 text-right font-medium">
              {p.ring_candidates.toLocaleString()}
            </td>
          </tr>
          {excludedRows.map(([k, n]) => (
            <tr key={k}>
              <td className="py-1 pl-4 text-gray-600">
                − {EXCLUSION_LABELS[k] ?? k}
              </td>
              <td className="py-1 text-right">{n.toLocaleString()}</td>
            </tr>
          ))}
          <tr className="bg-green-50">
            <td className="py-1 font-semibold text-gray-900">
              = New people to invite
            </td>
            <td
              className="py-1 text-right font-semibold"
              data-testid="eib-expand-cohort"
            >
              {p.cohort.toLocaleString()}
            </td>
          </tr>
          <tr>
            <td className="py-1 pl-4 text-gray-600">
              · inside the original {mi(p.bands.source_radius_mi)} (new since
              then)
            </td>
            <td className="py-1 text-right">
              {p.bands.inside_source.toLocaleString()}
            </td>
          </tr>
          {p.bands.covered_by_custom_wave !== null && (
            <tr>
              <td className="py-1 pl-4 text-gray-600">
                · {mi(p.bands.source_radius_mi)}–{mi(p.bands.middle_edge_mi)}{" "}
                (covered by a custom wave, measured)
              </td>
              <td className="py-1 text-right">
                {p.bands.covered_by_custom_wave.toLocaleString()}
              </td>
            </tr>
          )}
          <tr>
            <td className="py-1 pl-4 text-gray-600">
              · {mi(p.bands.middle_edge_mi ?? p.bands.source_radius_mi)}–
              {mi(p.bands.new_radius_mi)} (the new ring)
            </td>
            <td className="py-1 text-right">
              {p.bands.new_ring.toLocaleString()}
            </td>
          </tr>
          {p.bands.no_coordinates > 0 && (
            <tr>
              <td className="py-1 pl-4 text-gray-600">· no map location</td>
              <td className="py-1 text-right">
                {p.bands.no_coordinates.toLocaleString()}
              </td>
            </tr>
          )}
          <tr>
            <td className="py-1 pl-4 text-gray-600">
              · earlier waves’ never-mailed members included
            </td>
            <td className="py-1 text-right">
              {p.never_mailed.included.toLocaleString()}
            </td>
          </tr>
          <tr>
            <td className="py-1 text-gray-700">
              About how many will receive it (estimate)
            </td>
            <td className="py-1 text-right">
              {p.quality.will_reach_estimate.toLocaleString()}
            </td>
          </tr>
          <tr>
            <td className="py-1 text-gray-700">
              Event total after this (unique people invited)
            </td>
            <td className="py-1 text-right">
              {p.cumulative_total.toLocaleString()}
            </td>
          </tr>
          {p.registration.cap !== null && (
            <tr>
              <td className="py-1 text-gray-700">Registrations</td>
              <td className="py-1 text-right">
                {p.registration.registered} / {p.registration.cap}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {!req.include_never_mailed && p.never_mailed.total > 0 && (
        <p className="text-xs text-gray-600">
          Never reached by earlier invitations:{" "}
          {p.never_mailed.total.toLocaleString()} (about{" "}
          {p.never_mailed.about_reachable.toLocaleString()} reachable). Not
          included; start again with the option on to invite them.
        </p>
      )}
      {p.in_flight.length > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
          {p.in_flight.map((w) => (
            <p key={w.launch_id}>
              Wave {w.wave_number} has not gone out yet ({w.flight_status},{" "}
              {fmtDate(w.send_at, p.send.tz)}): its {w.size.toLocaleString()}{" "}
              people are left out of this group whatever the options say. If
              that wave is later cancelled, they are not invited by this
              expansion either; you will get an alert.
            </p>
          ))}
        </div>
      )}

      <div className="rounded-md border border-gray-200 p-3">
        <p className="font-medium text-gray-900">
          Invitation goes out: {p.send.send_at_local} ({p.send.tz})
        </p>
        <p className="text-xs text-gray-600">
          Chosen as {sendWhy}. Addresses not yet verified may take longer to
          receive it.
        </p>
        <p className="mt-2 font-medium text-gray-900">
          Follow-ups this group will get (Wave {p.source.cadence_wave}’s
          schedule)
        </p>
        {followups.length === 0 ? (
          <p className="text-xs text-gray-600">
            The original invitation had no follow-ups.
          </p>
        ) : (
          <ul className="text-xs text-gray-700">
            {followups.map((o) => (
              <li key={`${o.kind}-${o.offset_days}`}>
                {o.kind === "resend" ? "Invitation resend" : "Reminder"},{" "}
                {o.offset_days === 0
                  ? "day of the event"
                  : `${o.offset_days} day${o.offset_days === 1 ? "" : "s"} before`}{" "}
                ({fmtDate(o.due_at, p.send.tz)}):{" "}
                {o.kept ? "kept" : <strong>skipped — {o.why}</strong>}
              </li>
            ))}
          </ul>
        )}
        {p.kept_followups === 0 && (
          <p className="mt-1 text-xs font-medium text-amber-800">
            This group gets the invitation only.
          </p>
        )}
        <p className="mt-2 text-xs text-gray-600">
          Check-in:{" "}
          {p.contract.workflow_mode === "show_up" ? "show up" : "pre-register"}
          {p.contract.expected_capacity
            ? `, capacity ${p.contract.expected_capacity}`
            : ""}
          {p.contract.guest_award_enabled ? ", guest award on" : ""}. Copied
          from the original invitation.
        </p>
      </div>
    </div>
  );
}

function LiveRequest({
  req,
  onChanged,
}: {
  req: ExpansionRequest;
  onChanged: () => void;
}) {
  const p = req.proposal;
  const [ackCoverage, setAckCoverage] = useState(false);
  const [ackNoFollowups, setAckNoFollowups] = useState(false);
  const [ackVenue, setAckVenue] = useState(false);
  const [ackCheckin, setAckCheckin] = useState(false);
  const [typed, setTyped] = useState("");
  const [stepError, setStepError] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: () => cancelExpansion(req.id),
    onSuccess: onChanged,
  });
  const confirm = useMutation({
    mutationFn: async () => {
      setStepError(null);
      const res = await confirmExpansion({
        requestId: req.id,
        proposalDigest: req.proposal_digest ?? "",
        typedTotal: typed ? Number(typed) : null,
        ackNoFollowups,
        ackVenueChange: ackVenue,
        ackCheckin,
        ackRadiusWithinCoverage: ackCoverage,
      });
      const launchId = res.launch_id ?? res.expansion_launch_id;
      const templateId = res.template_id ?? req.template_id;
      if (launchId && templateId) {
        await verifyExpansionDesign(launchId, templateId);
      }
      return res;
    },
    onSettled: onChanged,
    onError: (e) => setStepError(expansionErrorText(e)),
  });
  const reverify = useMutation({
    mutationFn: () =>
      verifyExpansionDesign(
        req.expansion_launch_id ?? "",
        req.template_id ?? "",
      ),
    onError: (e) => setStepError(expansionErrorText(e)),
    onSuccess: () => setStepError(null),
  });

  if (req.status === "building" || req.status === "previewing") {
    return (
      <div
        className="flex items-center gap-3 text-sm text-gray-700"
        data-testid="eib-expand-building"
      >
        <LoadingSpinner size="sm" />
        <span>
          {req.status === "building"
            ? `Building the ${mi(req.new_radius_mi)} audience…`
            : "Recalculating the numbers…"}{" "}
          This can take a few minutes. You can leave this page.
          {req.attempt_no > 1 && req.last_refresh_reason && (
            <span className="block text-xs text-amber-800">
              Numbers changed since the last preview: {req.last_refresh_reason}.
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => cancel.mutate()}
          className="ml-auto rounded-md border border-gray-300 px-3 py-1 text-xs"
        >
          Cancel
        </button>
      </div>
    );
  }
  if (req.status === "confirmed") {
    return (
      <div
        className="space-y-2 text-sm text-gray-700"
        data-testid="eib-expand-finalising"
      >
        <div className="flex items-center gap-3">
          <LoadingSpinner size="sm" />
          <span>
            Finalising… usually within a minute or two. Nothing is sent until
            this finishes.
          </span>
        </div>
        {stepError && (
          <div className="rounded-md bg-red-50 p-2 text-xs text-red-800">
            {stepError}
          </div>
        )}
        {req.is_mine && (
          <p className="text-xs text-gray-600">
            Still waiting after a few minutes (for example after reloading the
            page)?{" "}
            <button
              type="button"
              className="underline"
              disabled={reverify.isPending}
              onClick={() => reverify.mutate()}
            >
              {reverify.isPending
                ? "Checking the design…"
                : "Check the invitation design again"}
            </button>
            . It must be checked within 15 minutes of confirming.
          </p>
        )}
        <button
          type="button"
          onClick={() => cancel.mutate()}
          className="rounded-md border border-gray-300 px-3 py-1 text-xs"
        >
          Cancel this expansion
        </button>
        {cancel.error && (
          <p className="text-xs text-red-700">
            {expansionErrorText(cancel.error)}
          </p>
        )}
      </div>
    );
  }
  if (req.status !== "ready" || !p) {
    return null;
  }

  const needsTyped = p.typed_total_required;
  const canConfirm =
    req.is_mine &&
    (!p.coverage_warning || ackCoverage) &&
    (p.kept_followups > 0 || ackNoFollowups) &&
    (!p.venue_changed || ackVenue) &&
    (p.contract.workflow_mode !== "show_up" || ackCheckin) &&
    (!needsTyped || Number(typed) === p.cumulative_total);

  return (
    <div className="space-y-3" data-testid="eib-expand-ready">
      {req.attempt_no > 1 && (
        <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
          Numbers changed since you last looked
          {req.last_refresh_reason ? ` (${req.last_refresh_reason})` : ""}.
          Nothing was reserved or sent. Review the new preview below.
        </p>
      )}
      <PreviewTable p={p} req={req} />
      <div className="space-y-2 text-sm">
        {p.coverage_warning && (
          <label className="flex items-start gap-2 cursor-pointer text-amber-900">
            <input
              type="checkbox"
              className="mt-1"
              checked={ackCoverage}
              onChange={(e) => setAckCoverage(e.target.checked)}
            />
            I understand {mi(req.new_radius_mi)} is not wider than the area
            already covered ({p.headline.headline_mi} mi); this mostly finds
            people who joined or became eligible since.
          </label>
        )}
        {p.kept_followups === 0 && (
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={ackNoFollowups}
              onChange={(e) => setAckNoFollowups(e.target.checked)}
            />
            I understand this group gets the invitation only (every follow-up
            has already passed for them).
          </label>
        )}
        {p.venue_changed && (
          <label className="flex items-start gap-2 cursor-pointer text-amber-900">
            <input
              type="checkbox"
              className="mt-1"
              checked={ackVenue}
              onChange={(e) => setAckVenue(e.target.checked)}
            />
            The event’s venue changed after the first invitation. I confirm the
            radius is measured from the current location.
          </label>
        )}
        {p.contract.workflow_mode === "show_up" && (
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={ackCheckin}
              onChange={(e) => setAckCheckin(e.target.checked)}
            />
            I understand public self check-in stays open on the event’s day, as
            for the original invitation.
          </label>
        )}
        {needsTyped && (
          <div>
            <label className="text-xs text-gray-700" htmlFor="eib-expand-typed">
              This takes the event above 25,000 people. Type the new event total
              ({p.cumulative_total.toLocaleString()}) to confirm:
            </label>
            <input
              id="eib-expand-typed"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="ml-2 w-32 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
        )}
      </div>
      {(stepError || confirm.error) && (
        <p className="text-sm text-red-700">{stepError}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!canConfirm || confirm.isPending}
          onClick={() => confirm.mutate()}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          data-testid="eib-expand-confirm"
          title={
            req.is_mine
              ? undefined
              : `Prepared by ${req.created_by_name ?? "someone else"}. Cancel it to prepare your own.`
          }
        >
          {confirm.isPending
            ? "Confirming…"
            : `Invite ${p.cohort.toLocaleString()} new ${p.cohort === 1 ? "person" : "people"}`}
        </button>
        <button
          type="button"
          onClick={() => cancel.mutate()}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm"
        >
          Cancel
        </button>
        {!req.is_mine && (
          <span className="text-xs text-gray-600">
            Prepared by {req.created_by_name ?? "someone else"}. Only they can
            confirm it; cancel it to prepare your own.
          </span>
        )}
      </div>
      {cancel.error && (
        <p className="text-xs text-red-700">
          {expansionErrorText(cancel.error)}
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------------------------
// History
// -----------------------------------------------------------------------------------------------
function HistoryRow({ req }: { req: ExpansionRequest }) {
  const r = req.result ?? {};
  if (req.status === "committed") {
    const o = req.outcomes;
    const outcome = r.outcome as string | undefined;
    return (
      <li
        className="flex items-start gap-2 text-sm"
        data-testid="eib-expand-history-committed"
      >
        <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
        <div>
          <p className="text-gray-900">
            Radius expansion {r.expansion_number ?? ""} ({mi(req.new_radius_mi)}
            ) — {Number(r.audience_count ?? 0).toLocaleString()}{" "}
            {Number(r.audience_count ?? 0) === 1 ? "person" : "people"} added
            {req.voided ? " (voided)" : ""}
          </p>
          <p className="text-xs text-gray-600">
            {outcome === "EIB_EXPANSION_NOBODY_TO_SEND"
              ? "Nobody could be sent the invitation (all suppressed, invalid, opted out or already reached at their address)."
              : outcome === "EIB_EXPANSION_RECONCILIATION_FAILED"
                ? "The invitation was stopped: not everyone could be accounted for. Engineering has been alerted."
                : req.flight_status === "cancelled" ||
                    req.flight_status === "failed"
                  ? `Invitation ${req.flight_status}; nobody else will be sent it.`
                  : `Invitation ${req.flight_status === "sent" ? "sent" : req.flight_status === "sending" ? "sending" : `scheduled for ${fmtDate(req.scheduled_send_at ?? (r.send_at as string), req.proposal?.send?.tz)}`}.`}
            {o &&
            (o.sent ||
              o.skipped_at_send ||
              o.withheld ||
              o.omitted ||
              o.unexplained) ? (
              <>
                {" "}
                Sent {o.sent}; skipped at send {o.skipped_at_send}; held back
                (address already used) {o.withheld}; not sendable (no email,
                suppressed…) {o.omitted}
                {o.unexplained ? `; unaccounted for ${o.unexplained}` : ""}.
              </>
            ) : null}
          </p>
          <p className="text-xs text-gray-400">
            A list of who was skipped and why is coming in a later update.
          </p>
        </div>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-2 text-sm">
      <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <div>
        <p className="text-gray-800">
          {mi(req.new_radius_mi)} —{" "}
          {req.status === "cancelled" ? "cancelled" : "not completed"}
          <span className="text-xs text-gray-500">
            {" "}
            · {fmtDate(req.created_at)} by {req.created_by_name ?? "unknown"}
          </span>
        </p>
        {req.failure_message && (
          <p className="text-xs text-gray-600">{req.failure_message}</p>
        )}
      </div>
    </li>
  );
}

// -----------------------------------------------------------------------------------------------
// Panel
// -----------------------------------------------------------------------------------------------
export default function EibExpandRadiusPanel({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const {
    data: status,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["eib-expansion-status", eventId],
    queryFn: () => getExpansionStatus(eventId),
    enabled: !!eventId,
    retry: false,
    refetchInterval: (q) => {
      const s = q.state.data as ExpansionStatus | undefined;
      return s?.requests?.some(
        (r) =>
          LIVE.has(r.status) ||
          (r.status === "committed" && r.flight_status === "sending"),
      )
        ? 5000
        : false;
    },
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["eib-expansion-status", eventId] });
    qc.invalidateQueries({ queryKey: ["event-campaign-audiences", eventId] });
  };

  if (isLoading) {
    return null;
  }
  if (error || !status) {
    return null;
  } // non-staff or feature unavailable: the tab works without it
  if (!status.enabled && status.requests.length === 0) {
    return null;
  } // shipped dark
  if (status.waves.length === 0) {
    return null;
  } // no Event in a Box invitation on this event

  const live = status.requests.find((r) => LIVE.has(r.status)) ?? null;
  const history = status.requests.filter((r) => !LIVE.has(r.status));

  return (
    <section
      className="rounded-lg border border-purple-200 bg-white p-5"
      data-testid="eib-expand-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2">
          <ArrowsPointingOutIcon className="mt-0.5 h-5 w-5 text-purple-600" />
          <CoverageSummary status={status} />
        </div>
        {!live && !open && (
          <button
            type="button"
            disabled={!status.can_expand}
            onClick={() => setOpen(true)}
            className="shrink-0 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="eib-expand-button"
          >
            Expand invitation radius
          </button>
        )}
      </div>
      <p className="mt-2 flex items-start gap-1 text-xs text-gray-500">
        <InformationCircleIcon className="h-4 w-4 shrink-0" />
        Widening invites only people who have not been invited to this event yet
        (checked by person and by email address), with whichever follow-ups are
        still ahead. People already invited are not sent this invitation again.
        An ordinary additional wave from the setup wizard is not checked against
        it.
      </p>
      {!status.can_expand && !live && status.blockers.length > 0 && (
        <ul
          className="mt-2 space-y-0.5 text-xs text-gray-700"
          data-testid="eib-expand-blockers"
        >
          {status.blockers.map((b) => (
            <li key={b.code}>• {b.message}</li>
          ))}
        </ul>
      )}
      {open && !live && (
        <ExpandForm
          eventId={eventId}
          status={status}
          onDone={() => {
            setOpen(false);
            refresh();
          }}
          onCancel={() => setOpen(false)}
        />
      )}
      {live && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="mb-2 text-sm font-medium text-gray-900">
            Widening to {mi(live.new_radius_mi)}
            <span className="text-xs font-normal text-gray-500">
              {" "}
              · prepared by {live.created_by_name ?? "unknown"}
            </span>
          </p>
          {/* keyed by attempt: a refreshed proposal never inherits the previous acknowledgements */}
          <LiveRequest
            key={`${live.id}-${live.attempt_no}-${live.proposal_digest ?? ""}`}
            req={live}
            onChanged={refresh}
          />
        </div>
      )}
      {history.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            Earlier widenings
          </p>
          <ul className="space-y-2">
            {history.map((r) => (
              <HistoryRow key={r.id} req={r} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
